import { api } from './api';
import type {
  SiteResponse,
  SiteCreate,
  SiteUpdate,
  SiteCompliance,
  SiteInspectionsResponse,
  SiteInspectionItem,
  PaginatedResponse,
  TimeGranularity,
  SiteActivityTimelineResponse,
  AgingSitesResponse,
  ContactResponse,
  ContactCreate,
  ContactUpdate,
  ProjectType,
} from '../types/api';
import { ringsParam, type Ring, type ScopeCounts } from '../lib/siteScope';

type SiteFilters = {
  status?: string; // 'active', 'pending', 'inactive', etc.
  // Completed = stopped through the stop flow or a finished legacy project
  // (JIM-1027-15); the server reads it beside status.
  stopped?: boolean;
  state?: string;
  organizationId?: string;
  search?: string;
  page?: number;
  size?: number;
  // Which of my rings to show — inspecting, consulting, reachable (groups +
  // org). Non-admins only; the server ignores it for admins (JIM-0919-08/13).
  rings?: readonly Ring[];
  // Quick filter options
  recentlyActive?: boolean;
  needsAttention?: boolean;
  hasOverdueInspection?: boolean;
  // Aging filter options
  isAging?: boolean;
  agingThresholdDays?: number;
  assignedConsultantId?: string;
  pipelineStage?: string;
};

// Transformed inspection for frontend use (camelCase)
type TransformedInspection = {
  id: string;
  type: string;
  status: string;
  conductedDate: string | null;
  scheduledDate: string | null;
  inspectorId: string;
  inspectorName: string;
  complianceScore: number | null;
  violationsCount: number;
  findingsCount: number;
  followUpRequired: boolean;
  notes: string | null;
  createdAt: string | null;
  reportUrl: string | null;
  /** When a report on its ten-minute hold will be emailed (JIM-1003-05). */
  sendScheduledAt?: string | null;
};

type SiteInspections = {
  siteId: string;
  total: number;
  inspections: TransformedInspection[];
  upcomingInspections: TransformedInspection[];
  overdueInspections: TransformedInspection[];
};

// Transformed compliance for frontend use (supports both snake_case and camelCase)
type TransformedCompliance = SiteCompliance & {
  // camelCase aliases for frontend convenience
  siteId: string;
  complianceScore: number;
  lastInspectionDate: string | null;
  last_inspection_date: string | null;
  nextRequiredInspection: string | null;
  next_required_inspection: string | null;
  permitStatus: string;
  permitExpiration?: string;
  complianceStatus: string;
  // Wire truth (#336): regulations are objects ({name, jurisdiction, ...}),
  // not strings — the old string[] claim was false; runtime unchanged.
  applicableRegulations: SiteCompliance['applicable_regulations'];
};

// Actions required (corrective actions from findings)
export type ActionRequired = {
  id: string;
  item: string;
  corrective_action: string | null;
  description: string | null;
  category: string;
  condition: string;
  location: string | null;
  priority: 'critical' | 'high' | 'medium' | 'low';
  due_text: string;
  deadline: string | null;
  inspection_id: string;
  created_at: string | null;
  status: 'open' | 'corrected';
};

export type LastInspectionInfo = {
  id: string;
  date: string | null;
  inspector_name: string | null;
  status: string;
};

export type ActionsRequiredResponse = {
  site_id: string;
  actions: ActionRequired[];
  total: number;
  open_count: number;
  corrected_count: number;
  last_inspection: LastInspectionInfo | null;
};

// Unified corrective actions (from findings + extracted actions)
export type CorrectiveAction = {
  id: string;
  source: 'finding' | 'extracted';
  location: string;
  description: string;
  date_noted: string | null;
  verified_date: string | null;
  is_resolved: boolean;
  priority: 'critical' | 'high' | 'medium' | 'low' | 'resolved';
  due_text: string;
  deadline: string | null;
  inspection_id: string | null;
  category: string | null;
  item: string | null;
  condition: string | null;
};

export type CorrectiveActionsResponse = {
  site_id: string;
  site_name: string;
  corrective_actions: CorrectiveAction[];
  total: number;
  open_count: number;
  resolved_count: number;
  include_resolved: boolean;
};

// Combined site detail response for performance optimization
export type SiteDetailResponse = {
  site: SiteResponse;
  inspections: SiteInspections;
  correctiveActions: CorrectiveActionsResponse;
};

// Lightweight map data for performance (coordinates + basic info only)
export type SiteMapData = {
  id: string;
  name: string;
  /** Customer for the marker popup: per-site entity override, else client. */
  client_name?: string | null;
  coordinates: {
    latitude: number;
    longitude: number;
  } | null;
  status: string;
  assigned_inspector_name: string | null;
  swppp_archive?: boolean;
};

// Reports summary stats (aggregated data only)
export type ReportsSummaryData = {
  totalSites: number;
  totalInspections: number;
  pendingInspections: number;
  completedInspections: number;
  violationsCount: number;
  activeInspectors: number;
  sitesAssigned: number;
  sitesUnassigned: number;
};

export type SiteReportRecipient = {
  email: string;
  name: string;
  title?: string | null;
  can_sign: boolean;
};

export type SiteReportRecipients = {
  site_id: string;
  site_name: string;
  live: boolean;
  app_inspections: number;
  recipients: SiteReportRecipient[];
};

export type SiteSearchAllItem = {
  id: string;
  name: string;
  client_name: string | null;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
  assigned_inspector_id: string | null;
  assigned_inspector_name: string | null;
  reachable: boolean;
};

export type SiteSearchAllResponse = {
  items: SiteSearchAllItem[];
  total: number;
  page: number;
  size: number;
};

class SitesService {
  async getSites(
    filters: SiteFilters = {}
  ): Promise<PaginatedResponse<SiteResponse>> {
    const params = new URLSearchParams();

    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.stopped) {
      params.append('stopped', 'true');
    }
    if (filters.state) {
      params.append('state', filters.state);
    }
    if (filters.organizationId) {
      params.append('organization_id', filters.organizationId);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.size) {
      params.append('size', filters.size.toString());
    }
    // Quick filter options
    if (filters.recentlyActive !== undefined) {
      params.append('recently_active', filters.recentlyActive.toString());
    }
    if (filters.needsAttention !== undefined) {
      params.append('needs_attention', filters.needsAttention.toString());
    }
    if (filters.hasOverdueInspection !== undefined) {
      params.append(
        'has_overdue_inspection',
        filters.hasOverdueInspection.toString()
      );
    }
    // Aging filter options
    if (filters.isAging !== undefined) {
      params.append('is_aging', filters.isAging.toString());
    }
    if (filters.agingThresholdDays !== undefined) {
      params.append(
        'aging_threshold_days',
        filters.agingThresholdDays.toString()
      );
    }
    if (filters.assignedConsultantId) {
      params.append('assigned_consultant_id', filters.assignedConsultantId);
    }
    if (filters.pipelineStage) {
      params.append('pipeline_stage', filters.pipelineStage);
    }
    if (filters.rings) {
      params.append('rings', ringsParam(filters.rings));
    }

    const response = await api.get(`/sites/?${params.toString()}`);
    return response.data as PaginatedResponse<SiteResponse>;
  }

  /**
   * What each access ring contributes for the signed-in user. Feeds the
   * counts on the Show inspections / Show consulting / Show group sites
   * buttons on the map and the Sites page (JIM-0919-08/13).
   */
  /**
   * The staff-wide site search (Jim 10/27): every active site, not only the
   * ones the caller reaches. Rows with `reachable: false` need a Claim first.
   */
  async searchAllSites(
    q: string,
    page = 1,
    size = 20
  ): Promise<SiteSearchAllResponse> {
    const response = await api.get<SiteSearchAllResponse>('/sites/search-all', {
      params: { q, page, size },
    });
    return response.data;
  }

  async getScopeCounts(): Promise<ScopeCounts> {
    const response = await api.get<ScopeCounts>('/sites/scope-counts');
    return response.data;
  }

  async getSite(siteId: string): Promise<SiteResponse> {
    const response = await api.get(`/sites/${siteId}`);
    return response.data as SiteResponse;
  }

  async createSite(data: SiteCreate): Promise<SiteResponse> {
    const response = await api.post('/sites/', data);
    return response.data as SiteResponse;
  }

  async updateSite(siteId: string, data: SiteUpdate): Promise<SiteResponse> {
    const response = await api.patch(`/sites/${siteId}`, data);
    return response.data as SiteResponse;
  }

  /**
   * Narrow location-only update for the inspector site-detail drawer: address
   * and/or map coordinates. Backed by PATCH /sites/{id}/location, which only
   * accepts these fields and is allowed for inspectors (sites:update_location).
   */
  async updateSiteLocation(
    siteId: string,
    data: {
      address?: {
        street1?: string;
        street2?: string | null;
        city?: string;
        state?: string;
        zip_code?: string;
        county?: string | null;
      };
      coordinates?: { latitude: number; longitude: number };
    }
  ): Promise<SiteResponse> {
    const response = await api.patch(`/sites/${siteId}/location`, data);
    return response.data as SiteResponse;
  }

  // Inspector-safe quick edit of permit + estimated completion (#260).
  async updateSiteDetails(
    siteId: string,
    data: {
      permit_number?: string | null;
      permit_expiration_date?: string | null;
      estimated_end_date?: string | null;
      confirm_estimated_end_date?: boolean;
      /** Set from the inspection flow when the site has none (10/19). */
      project_type?: ProjectType;
    }
  ): Promise<SiteResponse> {
    const response = await api.patch(`/sites/${siteId}/details`, data);
    return response.data as SiteResponse;
  }

  async deleteSite(siteId: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string; site_id: string }>(
      `/sites/${siteId}`
    );
    return { message: response.data.message };
  }

  async getSiteDeleteImpact(siteId: string): Promise<{
    siteId: string;
    siteName: string;
    totalInspections: number;
    totalContacts: number;
    message: string;
  }> {
    const response = await api.get<{
      site_id: string;
      site_name: string;
      total_inspections: number;
      total_contacts: number;
      message: string;
    }>(`/sites/${siteId}/delete-impact`);
    const d = response.data;
    return {
      siteId: d.site_id,
      siteName: d.site_name,
      totalInspections: d.total_inspections,
      totalContacts: d.total_contacts,
      message: d.message,
    };
  }

  async restoreSite(siteId: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string; site_id: string }>(
      `/sites/${siteId}/restore`
    );
    return { message: response.data.message };
  }

  async assignInspector(
    siteId: string,
    inspectorId: string | null
  ): Promise<{
    id: string;
    name: string;
    assigned_inspector_id: string | null;
    assigned_inspector_name: string | null;
    assigned_inspector_email: string | null;
    message: string;
  }> {
    const response = await api.patch<{
      id: string;
      name: string;
      assigned_inspector_id: string | null;
      assigned_inspector_name: string | null;
      assigned_inspector_email: string | null;
      message: string;
    }>(`/sites/${siteId}/assign-inspector`, null, {
      params: { inspector_id: inspectorId },
    });
    return response.data;
  }

  async getConsultantOptions(
    siteId: string
  ): Promise<Array<{ id: string; name: string }>> {
    const response = await api.get<Array<{ id: string; name: string }>>(
      `/sites/${siteId}/consultant-options`
    );
    return response.data;
  }

  async assignConsultant(
    siteId: string,
    consultantId: string | null
  ): Promise<{
    id: string;
    name: string;
    assigned_consultant_id: string | null;
    assigned_consultant_name: string | null;
    message: string;
  }> {
    const response = await api.patch<{
      id: string;
      name: string;
      assigned_consultant_id: string | null;
      assigned_consultant_name: string | null;
      message: string;
    }>(`/sites/${siteId}/assign-consultant`, null, {
      params: { consultant_id: consultantId },
    });
    return response.data;
  }

  async getAgingSites(thresholdDays = 7): Promise<AgingSitesResponse> {
    const response = await api.get<AgingSitesResponse>(
      `/sites/my-aging-sites?aging_threshold_days=${thresholdDays}`
    );
    return response.data;
  }

  async getSiteInspections(
    siteId: string,
    limit = 10,
    offset = 0
  ): Promise<SiteInspections> {
    const response = await api.get(
      `/sites/${siteId}/inspections?limit=${limit}&offset=${offset}`
    );
    const data = response.data as SiteInspectionsResponse;

    // Transform API response (snake_case) to frontend format (camelCase)
    // Use conductor_name (extracted from documents) if available, fallback to inspector_name (from User)
    const inspections: TransformedInspection[] = (data.items || []).map(
      (item: SiteInspectionItem) => ({
        id: item.id,
        type: item.type,
        status: item.status,
        conductedDate: item.conducted_date ?? null,
        scheduledDate: item.scheduled_date ?? null,
        inspectorId: item.inspector_id ?? '',
        inspectorName: item.conductor_name || item.inspector_name || 'Unknown',
        complianceScore: item.compliance_score ?? null,
        violationsCount: item.violations_count,
        findingsCount: item.violations_count || 0,
        followUpRequired: item.follow_up_required,
        notes: item.notes ?? null,
        createdAt: item.created_at ?? null,
        reportUrl: item.report_url || null,
        sendScheduledAt: item.send_scheduled_at ?? null,
      })
    );

    // Separate upcoming and overdue based on status and dates
    const now = new Date();
    const upcomingInspections = inspections.filter(
      (i) =>
        i.status === 'scheduled' ||
        (i.scheduledDate &&
          new Date(i.scheduledDate) > now &&
          i.status !== 'completed' &&
          i.status !== 'approved')
    );
    const overdueInspections = inspections.filter(
      (i) =>
        i.scheduledDate &&
        new Date(i.scheduledDate) < now &&
        i.status === 'scheduled'
    );

    return {
      siteId: data.site_id || siteId,
      total: data.total ?? inspections.length,
      inspections,
      upcomingInspections,
      overdueInspections,
    };
  }

  async getSiteCompliance(siteId: string): Promise<TransformedCompliance> {
    const response = await api.get(`/sites/${siteId}/compliance`);
    const data = response.data as SiteCompliance;

    // Transform API response (snake_case) to frontend format (camelCase)
    // compliance_score from API is already 0-100
    const score =
      data.compliance_score != null ? Math.round(data.compliance_score) : 0;

    return {
      ...data,
      siteId: data.site_id || siteId,
      complianceScore: score,
      compliance_score: score,
      violations: data.violations || [],
      lastInspectionDate: data.last_inspection_date ?? null,
      last_inspection_date: data.last_inspection_date ?? null,
      nextRequiredInspection: data.next_required_inspection ?? null,
      next_required_inspection: data.next_required_inspection ?? null,
      permitStatus: data.permit_status ?? '',
      complianceStatus: data.compliance_status ?? '',
      applicableRegulations: data.applicable_regulations,
    };
  }

  async getSiteActivityTimeline(
    siteId: string,
    options: {
      granularity?: TimeGranularity;
      startDate?: string;
      endDate?: string;
    } = {}
  ): Promise<SiteActivityTimelineResponse> {
    const params = new URLSearchParams();
    if (options.granularity) {
      params.append('granularity', options.granularity);
    }
    if (options.startDate) {
      params.append('start_date', options.startDate);
    }
    if (options.endDate) {
      params.append('end_date', options.endDate);
    }

    const queryString = params.toString();
    const url = `/sites/${siteId}/activity-timeline${queryString ? `?${queryString}` : ''}`;
    const response = await api.get<SiteActivityTimelineResponse>(url);
    return response.data;
  }

  async getSiteActionsRequired(
    siteId: string,
    limit = 20,
    includeCorrected = true
  ): Promise<ActionsRequiredResponse> {
    const response = await api.get<ActionsRequiredResponse>(
      `/sites/${siteId}/actions-required?limit=${limit}&include_corrected=${includeCorrected}`
    );
    return response.data;
  }

  async getSiteCorrectiveActions(
    siteId: string,
    includeResolved = true,
    limit = 50
  ): Promise<CorrectiveActionsResponse> {
    const response = await api.get<CorrectiveActionsResponse>(
      `/sites/${siteId}/findings-needing-correction?include_resolved=${includeResolved}&limit=${limit}`
    );
    return response.data;
  }

  // Utility methods
  getStateOptions() {
    return [
      { value: 'AL', label: 'Alabama' },
      { value: 'AK', label: 'Alaska' },
      { value: 'AZ', label: 'Arizona' },
      { value: 'AR', label: 'Arkansas' },
      { value: 'CA', label: 'California' },
      { value: 'CO', label: 'Colorado' },
      { value: 'CT', label: 'Connecticut' },
      { value: 'DE', label: 'Delaware' },
      { value: 'DC', label: 'District of Columbia' },
      { value: 'FL', label: 'Florida' },
      { value: 'GA', label: 'Georgia' },
      { value: 'HI', label: 'Hawaii' },
      { value: 'ID', label: 'Idaho' },
      { value: 'IL', label: 'Illinois' },
      { value: 'IN', label: 'Indiana' },
      { value: 'IA', label: 'Iowa' },
      { value: 'KS', label: 'Kansas' },
      { value: 'KY', label: 'Kentucky' },
      { value: 'LA', label: 'Louisiana' },
      { value: 'ME', label: 'Maine' },
      { value: 'MD', label: 'Maryland' },
      { value: 'MA', label: 'Massachusetts' },
      { value: 'MI', label: 'Michigan' },
      { value: 'MN', label: 'Minnesota' },
      { value: 'MS', label: 'Mississippi' },
      { value: 'MO', label: 'Missouri' },
      { value: 'MT', label: 'Montana' },
      { value: 'NE', label: 'Nebraska' },
      { value: 'NV', label: 'Nevada' },
      { value: 'NH', label: 'New Hampshire' },
      { value: 'NJ', label: 'New Jersey' },
      { value: 'NM', label: 'New Mexico' },
      { value: 'NY', label: 'New York' },
      { value: 'NC', label: 'North Carolina' },
      { value: 'ND', label: 'North Dakota' },
      { value: 'OH', label: 'Ohio' },
      { value: 'OK', label: 'Oklahoma' },
      { value: 'OR', label: 'Oregon' },
      { value: 'PA', label: 'Pennsylvania' },
      { value: 'RI', label: 'Rhode Island' },
      { value: 'SC', label: 'South Carolina' },
      { value: 'SD', label: 'South Dakota' },
      { value: 'TN', label: 'Tennessee' },
      { value: 'TX', label: 'Texas' },
      { value: 'UT', label: 'Utah' },
      { value: 'VT', label: 'Vermont' },
      { value: 'VA', label: 'Virginia' },
      { value: 'WA', label: 'Washington' },
      { value: 'WV', label: 'West Virginia' },
      { value: 'WI', label: 'Wisconsin' },
      { value: 'WY', label: 'Wyoming' },
    ];
  }

  getProjectTypeOptions() {
    return [
      {
        value: 'residential',
        label: 'Residential',
        description: 'Homes, apartments, subdivisions',
      },
      {
        value: 'commercial',
        label: 'Commercial',
        description: 'Retail, office, industrial buildings',
      },
      {
        value: 'development',
        label: 'Development',
        description: 'Infrastructure, mixed-use, large projects',
      },
    ];
  }

  async updateProjectType(
    siteId: string,
    projectType: 'residential' | 'commercial' | 'development' | 'test' | null
  ): Promise<SiteResponse> {
    const response = await api.patch(`/sites/${siteId}`, {
      project_type: projectType,
    });
    return response.data as SiteResponse;
  }

  // SWPPP Methods

  /**
   * Generate Project Information Form (PIF) document
   */
  async generatePIF(
    siteId: string,
    format: 'pdf' | 'docx' = 'pdf'
  ): Promise<{
    url: string;
    attachment_id: string;
    format: string;
    size: number;
    file_name: string;
  }> {
    const response = await api.post<{
      url: string;
      attachment_id: string;
      format: string;
      size: number;
      file_name: string;
    }>(`/sites/${siteId}/generate-pif?format=${format}`);
    return response.data;
  }

  /**
   * Analyze SWPPP document using ML
   */
  async analyzeSWPPP(
    siteId: string,
    file: File
  ): Promise<{
    completeness: number;
    compliance: number;
    quality: number;
    confidence: number;
    model_type: string;
    features: {
      word_count: number;
      section_count: number;
      has_permit_number: boolean;
      has_project_name: boolean;
      has_bmps: boolean;
      has_inspection_schedule: boolean;
      mentions_epa: boolean;
      mentions_npdes: boolean;
      required_section_coverage: number;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<{
      completeness: number;
      compliance: number;
      quality: number;
      confidence: number;
      model_type: string;
      features: {
        word_count: number;
        section_count: number;
        has_permit_number: boolean;
        has_project_name: boolean;
        has_bmps: boolean;
        has_inspection_schedule: boolean;
        mentions_epa: boolean;
        mentions_npdes: boolean;
        required_section_coverage: number;
      };
    }>(`/sites/${siteId}/analyze-document`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Get complete site detail data in a single call for better performance
   * Combines site data, inspections, and corrective actions
   * @param siteId - Site ID
   * @param inspectionLimit - Max inspections to fetch
   * @param actionLimit - Max corrective actions to fetch
   * @returns Combined site detail data
   */
  async getSiteDetail(
    siteId: string,
    inspectionLimit = 10,
    actionLimit = 50
  ): Promise<SiteDetailResponse> {
    // Use Promise.all to fetch all data in parallel for better performance
    const [site, inspections, correctiveActions] = await Promise.all([
      this.getSite(siteId),
      this.getSiteInspections(siteId, inspectionLimit),
      this.getSiteCorrectiveActions(siteId, true, actionLimit),
    ]);

    return {
      site,
      inspections,
      correctiveActions,
    };
  }

  /**
   * Get lightweight map data for performance (coordinates + basic info only)
   * Optimized for heat maps and visualizations - 90% less data than full site objects
   * @returns Array of site map data (coordinates + essential info only)
   */
  async getSiteMapData(rings?: readonly Ring[]): Promise<SiteMapData[]> {
    const response = await api.get<
      {
        id: string;
        name: string;
        client_name?: string | null;
        latitude: number;
        longitude: number;
        status: string;
        assigned_inspector_id: string | null;
        assigned_inspector_name: string | null;
        swppp_archive?: boolean;
      }[]
    >('/sites/map-points', {
      params: rings ? { rings: ringsParam(rings) } : undefined,
    });

    return response.data.map(
      (point): SiteMapData => ({
        id: point.id,
        name: point.name,
        client_name: point.client_name ?? null,
        coordinates:
          point.latitude && point.longitude
            ? { latitude: point.latitude, longitude: point.longitude }
            : null,
        status: point.status,
        assigned_inspector_name: point.assigned_inspector_name,
        swppp_archive: point.swppp_archive ?? false,
      })
    );
  }

  /**
   * Get aggregated reports summary data for performance
   * Returns pre-calculated stats instead of raw data for processing
   * @returns Aggregated summary statistics
   */
  async getReportsSummary(): Promise<ReportsSummaryData> {
    // For now, calculate from existing endpoints
    // TODO: Create backend endpoint that returns pre-aggregated stats
    const response = await api.get<{
      total: number;
      by_status: Record<string, number>;
      active: number;
      pending: number;
      completed: number;
      sites_assigned: number;
      sites_unassigned: number;
      active_inspectors: number;
    }>('/sites/stats');

    const stats = response.data;
    return {
      totalSites: stats.total,
      totalInspections: 0, // Populated by inspection service
      pendingInspections: 0,
      completedInspections: 0,
      violationsCount: 0,
      activeInspectors: stats.active_inspectors,
      sitesAssigned: stats.sites_assigned,
      sitesUnassigned: stats.sites_unassigned,
    };
  }

  // Contact Management Methods

  /**
   * Who a report sent from this site reaches, resolved the way the send
   * resolves it, plus how many inspections the site has filed in the app
   * (0 = the next one is its first; Jim 10/21 asks for a recipients check).
   */
  async getReportRecipients(siteId: string): Promise<SiteReportRecipients> {
    const response = await api.get<SiteReportRecipients>(
      `/sites/${siteId}/report-recipients`
    );
    return response.data;
  }

  /**
   * Get all contacts for a site
   * @param siteId - Site ID
   * @param includeInactive - Include inactive contacts (default: false)
   * @returns Array of contacts
   */
  async getContacts(
    siteId: string,
    includeInactive = false
  ): Promise<ContactResponse[]> {
    const response = await api.get<ContactResponse[]>(
      `/sites/${siteId}/contacts?include_inactive=${includeInactive}`
    );
    return response.data;
  }

  /**
   * Add a new contact to a site
   * @param siteId - Site ID
   * @param data - Contact creation data
   * @returns Created contact response
   */
  async addContact(
    siteId: string,
    data: ContactCreate
  ): Promise<{ success: boolean; message: string; contact: ContactResponse }> {
    const response = await api.post<{
      success: boolean;
      message: string;
      contact: ContactResponse;
    }>(`/sites/${siteId}/contacts`, data);
    return response.data;
  }

  /**
   * Update a contact on a site
   * @param siteId - Site ID
   * @param contactId - Contact ID
   * @param data - Contact update data
   * @returns Updated contact response
   */
  async updateContact(
    siteId: string,
    contactId: string,
    data: ContactUpdate
  ): Promise<{ success: boolean; message: string; contact: ContactResponse }> {
    const response = await api.patch<{
      success: boolean;
      message: string;
      contact: ContactResponse;
    }>(`/sites/${siteId}/contacts/${contactId}`, data);
    return response.data;
  }

  /**
   * Delete (deactivate) a contact from a site
   * @param siteId - Site ID
   * @param contactId - Contact ID
   * @param hardDelete - Permanently delete instead of soft delete
   * @returns Deletion result
   */
  async deleteContact(
    siteId: string,
    contactId: string,
    hardDelete = false
  ): Promise<{ success: boolean; message: string; contact_id: string }> {
    const response = await api.delete<{
      success: boolean;
      message: string;
      contact_id: string;
    }>(`/sites/${siteId}/contacts/${contactId}?hard_delete=${hardDelete}`);
    return response.data;
  }

  /**
   * Search contacts across all sites (deduplicated by email)
   */
  async searchContacts(
    q: string,
    role?: string,
    limit = 20
  ): Promise<(ContactResponse & { site_id: string })[]> {
    const params = new URLSearchParams({ q, limit: String(limit) });
    if (role) params.set('role', role);
    const response = await api.get<(ContactResponse & { site_id: string })[]>(
      `/sites/contacts/search?${params.toString()}`
    );
    return response.data;
  }
}

export const sitesService = new SitesService();
